import * as React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Dashboard from "./pages/dashboard";
import DashboardPaid from "./pages/dashboard-paid";
import DashboardOverview from "./pages/dashboard/index";
import Form from "./pages/form";
import Home from "./pages/home";
import NotSubscribed from "./pages/not-subscribed";
import LockedOutPage from "./pages/locked-out";
import Success from "./pages/success";
import Contact from "./pages/contact";
import LeadList from "./pages/leads";
import LeadView from "./pages/leads/[id]";

import BidList from "./pages/bids";
import BidView from "./pages/bids/[id]";
import ArchivedBidsPage from "./pages/bids/archived";
import ClientList from "./pages/clients";
import ClientView from "./pages/clients/[id]";
import ArchivedClientsPage from "./pages/clients/archived";
import ServiceLocationList from "./pages/clients/locations";
import ServiceLocationView from "./pages/clients/locations/[id]";
import ArchivedLocationsPage from "./pages/clients/locations/archived";
import ClientOnboardingList from "./pages/clients/onboarding";
import ClientReviews from "./pages/reviews";
import PublicReviewSubmissionPage from "./pages/reviews/submit/[token]";
import SubcontractorList from "./pages/subcontractors";
import SubcontractorView from "./pages/subcontractors/[id]";
import ArchivedSubcontractorsPage from "./pages/subcontractors/archived";
import CompanyProfilePage from "./pages/company-profile";
import DocumentList from "./pages/documents";
import DocumentSigningWorkflowPage from "./pages/documents/signing-workflow/[id]";
import TemplateList from "./pages/templates";
import DocumentWriter from "./pages/document-writer";
import EmployeesPage from "./pages/employees";
import EmployeeView from "./pages/employees/[id]";
import ArchivedEmployeesPage from "./pages/employees/archived";
import EquipmentPage from "./pages/equipment";
import SuppliesPage from "./pages/supplies";
import AgentModePage from "./pages/agent-mode";

import CalendarPage from "./pages/calendar";
import MessagesPage from "./pages/messages";
import TeamCalendarPage from "./pages/calendar/team";
import ProjectSchedulesPage from "./pages/project-schedules";
import AutomationHubPage from "./pages/automation-hub";
import AutomationAlertsPage from "./pages/automation-hub/Alerts";
import WorkflowsDashboard from "./pages/workflows/index";
import WorkflowBuilderPage from "./pages/workflows/builder";
import ArchivedWorkflowsPage from "./pages/workflows/archived";
import ProtectedRoute from "./components/wrappers/ProtectedRoute";
import { LoadingSpinner } from "./components/loading-spinner";
import {
  RouteGuard,
  UnauthorizedAccess,
  ProtectedRoute as NewProtectedRoute,
  AdminRouteGuard,
  ManagerRouteGuard,
  AnalyticsRouteGuard,
  TeamManagementRouteGuard,
} from "./components/auth";
import AnalyticsPage from "./pages/analytics";

import GeneralSettings from "./pages/settings";
import ConnectionsPage from "./pages/settings/connections";
import UsersRolesPage from "./pages/settings/users";
import CompanyPage from "./pages/settings/company";
import BillingPage from "./pages/settings/billing";
import KnowledgeBasePage from "./pages/knowledge-base";

// Import the ClientOnboardingView component directly
import ClientOnboardingView from "./pages/clients/onboarding/[id]";
// Import the SignIn, SignUp and CreateOrganization components
import SignInPage from "./pages/sign-in";
import SignUpPage from "./pages/sign-up";
import CreateOrganizationPage from "./pages/create-organization";
import OnboardingPage from "./pages/onboarding";

import { BidBuilder } from "./pages/bids/BidBuilder";
import { Toaster } from "sonner";
import { Toaster as RadixToaster } from "@/components/ui/toaster";
import { useOrganizationSync } from "./hooks/useOrganizationSync";
import { useStoreUserEffect } from "./utils/useStoreUserEffect";
// NOTE: ChatHistoryProvider removed - deprecated and not used by active components
// The active sidebar (RefactoredAgentChatSidebar) uses sidebarChatStreaming directly
// import { ChatHistoryProvider } from "./contexts/ChatHistoryContext";
import { AgentSidebarProvider } from "./contexts/AgentSidebarContext";
import { AgentCanvasProvider } from "./contexts/AgentCanvasContext";
import { DocumentWriterProvider } from "./contexts/DocumentWriterContext";
import { FocusModeProvider } from "./contexts/FocusModeContext";
import ClientManagementPage from "./pages/features/client-management";
import ServiceSchedulingPage from "./pages/features/service-scheduling";
import StaffManagementPage from "./pages/features/staff-management";
import BidsManagementPage from "./pages/features/bids-management";
import PricingPage from "./pages/pricing";
import PrivacyPolicy from "./pages/privacy";
import TermsOfService from "./pages/terms";
import { ScrollToTop } from "./components/scroll-to-top";
import BiddingCalculatorPage from "./pages/tools/bidding-calculator";

// Success Hub pages
import SuccessHubPage from "./pages/success-hub";
import GettingStartedPage from "./pages/success-hub/getting-started";
import ArticleDetailPage from "./pages/success-hub/article-detail";
import BestPracticesPage from "./pages/success-hub/best-practices";
import SalesLeadGenPage from "./pages/success-hub/sales-lead-gen";
import TroubleshootingPage from "./pages/success-hub/troubleshooting";
import QualityRetentionPage from "./pages/success-hub/quality-retention";
import TipsGuidesPage from "./pages/success-hub/tips-guides";
import StaffCompliancePage from "./pages/success-hub/staff-compliance";
import FAQPage from "./pages/success-hub/faq";

// Platform Admin pages

// Support page
import SupportPage from "./pages/settings/SupportPage";

// Public Proposal Viewer
import ProposalViewer from "./pages/proposal/ProposalViewer";

// Public Document Signing
import PublicDocumentSignPage from "./pages/public/sign/[id]";

// Public Canvas Viewer
import PublicCanvasViewer from "./pages/public/canvas/[shareToken]";

function App() {
  // Ensure Clerk organization is synced with local database
  useOrganizationSync();
  // Ensure user is stored in Convex database upon sign-in
  useStoreUserEffect();

  return (
    <FocusModeProvider>
      <AgentSidebarProvider>
        <AgentCanvasProvider>
          <DocumentWriterProvider>
            <Toaster
              toastOptions={{
                style: {
                  background: "hsl(var(--primary))",
                  color: "white",
                  border: "none",
                },
                className: "text-white",
              }}
            />
            <RadixToaster />
            <ScrollToTop />

            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/features/client-management" element={<ClientManagementPage />} />
              <Route path="/features/service-scheduling" element={<ServiceSchedulingPage />} />
              <Route path="/features/staff-management" element={<StaffManagementPage />} />
              <Route path="/features/bids-management" element={<BidsManagementPage />} />

              {/* Free Tools */}
              <Route path="/tools/commercial-cleaning-bid-calculator" element={<BiddingCalculatorPage />} />
              <Route path="/tools/bidding-calculator" element={<BiddingCalculatorPage />} />

              {/* Public Proposal Viewer */}
              <Route path="/p/:shareToken" element={<ProposalViewer />} />

              {/* Public Document Signing */}
              <Route path="/sign/:id" element={<PublicDocumentSignPage />} />

              {/* Public Canvas Viewer */}
              <Route path="/canvas/:shareToken" element={<PublicCanvasViewer />} />

              {/* Success Hub Routes */}
              <Route path="/success-hub" element={<SuccessHubPage />} />
              <Route path="/success-hub/getting-started" element={<GettingStartedPage />} />
              <Route path="/success-hub/sales-lead-gen" element={<SalesLeadGenPage />} />
              <Route path="/success-hub/article/:slug" element={<ArticleDetailPage />} />
              <Route path="/success-hub/best-practices" element={<BestPracticesPage />} />
              <Route path="/success-hub/troubleshooting" element={<TroubleshootingPage />} />
              <Route path="/success-hub/quality-retention" element={<QualityRetentionPage />} />
              <Route path="/success-hub/staff-compliance" element={<StaffCompliancePage />} />
              <Route path="/success-hub/tips-guides" element={<TipsGuidesPage />} />
              <Route path="/success-hub/faq" element={<FAQPage />} />

              <Route
                path="/dashboard"
                element={
                  <NewProtectedRoute>
                    <DashboardOverview />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/dashboard-old"
                element={
                  <NewProtectedRoute>
                    <Dashboard />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/dashboard-paid"
                element={
                  <NewProtectedRoute>
                    <DashboardPaid />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/leads"
                element={
                  <NewProtectedRoute requiredPermission="leads:read">
                    <LeadList />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/leads/:id"
                element={
                  <NewProtectedRoute requiredPermission="leads:read">
                    <LeadView />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/bids"
                element={
                  <NewProtectedRoute requiredPermission="bids:read">
                    <BidList />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/bids/:id"
                element={
                  <NewProtectedRoute requiredPermission="bids:read">
                    <BidView />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/bids/archived"
                element={
                  <NewProtectedRoute requiredPermission="bids:read">
                    <ArchivedBidsPage />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/clients"
                element={
                  <NewProtectedRoute requiredPermission="clients:read">
                    <ClientList />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/clients/archived"
                element={
                  <NewProtectedRoute requiredPermission="clients:read">
                    <ArchivedClientsPage />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/clients/:id"
                element={
                  <NewProtectedRoute requiredPermission="clients:read">
                    <ClientView />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/clients/locations"
                element={
                  <NewProtectedRoute requiredPermission="clients:read">
                    <ServiceLocationList />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/clients/:id/locations/archived"
                element={
                  <NewProtectedRoute requiredPermission="clients:read">
                    <ArchivedLocationsPage />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/clients/locations/:id"
                element={
                  <NewProtectedRoute requiredPermission="clients:read">
                    <ServiceLocationView />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/clients/onboarding"
                element={
                  <NewProtectedRoute requiredPermission="clients:read">
                    <ClientOnboardingList />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/clients/onboarding/:id"
                element={
                  <NewProtectedRoute requiredPermission="clients:read">
                    <ClientOnboardingView />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/reviews"
                element={
                  <NewProtectedRoute requiredPermission="clients:read">
                    <ClientReviews />
                  </NewProtectedRoute>
                }
              />
              <Route path="/reviews/submit/:token" element={<PublicReviewSubmissionPage />} />
              <Route
                path="/subcontractors"
                element={
                  <NewProtectedRoute>
                    <SubcontractorList />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/subcontractors/archived"
                element={
                  <NewProtectedRoute>
                    <ArchivedSubcontractorsPage />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/subcontractors/:id"
                element={
                  <NewProtectedRoute>
                    <SubcontractorView />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/company-profile"
                element={
                  <NewProtectedRoute>
                    <CompanyProfilePage />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/documents"
                element={
                  <NewProtectedRoute>
                    <DocumentList />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/documents/:id/signing-workflow"
                element={
                  <NewProtectedRoute>
                    <DocumentSigningWorkflowPage />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/document-writer"
                element={
                  <NewProtectedRoute>
                    <DocumentWriter />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/employees"
                element={
                  <TeamManagementRouteGuard>
                    <EmployeesPage />
                  </TeamManagementRouteGuard>
                }
              />
              <Route
                path="/employees/archived"
                element={
                  <TeamManagementRouteGuard>
                    <ArchivedEmployeesPage />
                  </TeamManagementRouteGuard>
                }
              />
              <Route
                path="/employees/:id"
                element={
                  <TeamManagementRouteGuard>
                    <EmployeeView />
                  </TeamManagementRouteGuard>
                }
              />
              <Route
                path="/equipment"
                element={
                  <NewProtectedRoute>
                    <EquipmentPage />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/supplies"
                element={
                  <NewProtectedRoute>
                    <SuppliesPage />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/automation-hub"
                element={<Navigate to="/agent-mode" replace />}
              />
              <Route
                path="/automation-hub/alerts"
                element={<Navigate to="/agent-mode" replace />}
              />
              <Route
                path="/workflows"
                element={
                  <NewProtectedRoute>
                    <WorkflowsDashboard />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/workflows/builder"
                element={
                  <NewProtectedRoute>
                    <WorkflowBuilderPage />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/workflows/archived"
                element={
                  <NewProtectedRoute>
                    <ArchivedWorkflowsPage />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/messages"
                element={
                  <NewProtectedRoute>
                    <MessagesPage />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/calendar"
                element={
                  <NewProtectedRoute>
                    <CalendarPage />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/project-schedules"
                element={
                  <NewProtectedRoute requiredPermission="clients:read">
                    <ProjectSchedulesPage />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/calendar/team"
                element={
                  <TeamManagementRouteGuard>
                    <TeamCalendarPage />
                  </TeamManagementRouteGuard>
                }
              />
              <Route
                path="/analytics"
                element={
                  <AnalyticsRouteGuard>
                    <AnalyticsPage />
                  </AnalyticsRouteGuard>
                }
              />

              <Route
                path="/settings"
                element={
                  <NewProtectedRoute requiredPermission="settings:read">
                    <GeneralSettings />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/settings/connections"
                element={
                  <NewProtectedRoute requiredPermission="settings:read">
                    <ConnectionsPage />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/settings/users"
                element={
                  <NewProtectedRoute requiredPermission="team:read">
                    <UsersRolesPage />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/settings/company"
                element={
                  <NewProtectedRoute requiredPermission="settings:read">
                    <CompanyPage />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/settings/billing"
                element={
                  <NewProtectedRoute requiredPermission="settings:read">
                    <BillingPage />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/knowledge-base"
                element={
                  <NewProtectedRoute>
                    <KnowledgeBasePage />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/dashboard/help/support"
                element={
                  <NewProtectedRoute>
                    <SupportPage />
                  </NewProtectedRoute>
                }
              />

              {/* Platform Admin Routes */}

              <Route
                path="/form"
                element={
                  <NewProtectedRoute>
                    <Form />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/templates"
                element={
                  <NewProtectedRoute>
                    <TemplateList />
                  </NewProtectedRoute>
                }
              />
              <Route path="/contact" element={<Contact />} />
              <Route path="/success" element={<Success />} />
              <Route path="/not-subscribed" element={<NotSubscribed />} />
              <Route path="/sign-in" element={<SignInPage />} />
              <Route path="/sign-up" element={<SignUpPage />} />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/create-organization" element={<CreateOrganizationPage />} />
              <Route path="/unauthorized" element={<UnauthorizedAccess />} />
              <Route path="/locked-out" element={<LockedOutPage />} />
              <Route
                path="/agent-mode"
                element={
                  <NewProtectedRoute>
                    <AgentModePage />
                  </NewProtectedRoute>
                }
              />
              <Route
                path="/bids/:bidId?"
                element={
                  <NewProtectedRoute requiredPermission="bids:write">
                    <BidBuilder />
                  </NewProtectedRoute>
                }
              />
            </Routes>
          </DocumentWriterProvider>
        </AgentCanvasProvider>
      </AgentSidebarProvider>
    </FocusModeProvider>
  );
}

export default App;
